import { useState } from "react";
import { toast } from "react-toastify";
import { useAdminInviteControllerResendInvite } from "service";
import { AdminAccountResDto } from "service/api/estabrookSchemas";

export interface UseResendInviteConfirm {
  pendingMember: AdminAccountResDto | null;
  isSubmitting: boolean;
  requestResend: (member: AdminAccountResDto) => void;
  confirmResend: () => void;
  cancelResend: () => void;
}

const useResendInviteConfirm = (): UseResendInviteConfirm => {
  const [pendingMember, setPendingMember] = useState<AdminAccountResDto | null>(
    null,
  );
  // IDs with a resend currently in flight. `isLoading` on the mutation itself
  // is shared across every member, so it can't tell "a request is running"
  // from "a request is running for the member whose dialog happens to be
  // open right now" — this does, and also lets the dialog auto-close only
  // when its own member's request is the one that resolved.
  const [submittingMemberIds, setSubmittingMemberIds] = useState<Set<string>>(
    new Set(),
  );

  const { mutate } = useAdminInviteControllerResendInvite({
    onSuccess: (data, variables) => {
      toast.success(`Invite resent to ${data.data?.email}`);
      const resentId = variables.pathParams.id;
      setPendingMember((current) =>
        current?.id === resentId ? null : current,
      );
      setSubmittingMemberIds((current) => {
        const next = new Set(current);
        next.delete(resentId);
        return next;
      });
    },
    onError: (_error, variables) => {
      toast.error("Couldn't resend the invite. Please try again.");
      setSubmittingMemberIds((current) => {
        const next = new Set(current);
        next.delete(variables.pathParams.id);
        return next;
      });
    },
  });

  const requestResend = (member: AdminAccountResDto) =>
    setPendingMember(member);
  const cancelResend = () => setPendingMember(null);
  const confirmResend = () => {
    if (!pendingMember) return;
    setSubmittingMemberIds((current) => new Set(current).add(pendingMember.id));
    mutate({ pathParams: { id: pendingMember.id } });
  };

  return {
    pendingMember,
    isSubmitting: pendingMember
      ? submittingMemberIds.has(pendingMember.id)
      : false,
    requestResend,
    confirmResend,
    cancelResend,
  };
};

export default useResendInviteConfirm;
